import { messageErreur } from '../api/errors';
import { publicApi } from '../api/publicClient';

/**
 * Session admin (cahier des charges §5) :
 * - jeton d'accès en mémoire seulement (15 min côté API), renouvelé avant expiration ;
 * - jeton de renouvellement en sessionStorage : survit au rechargement, pas à la
 *   fermeture de l'onglet ;
 * - déconnexion après 8 h sans activité.
 *
 * Module sans React : l'état est lu par useSession() (AuthProvider) et par le
 * client API, qui demande un jeton frais à chaque appel.
 */

export interface AdminUser {
  id: string;
  email: string;
  username?: string | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /** Double authentification activée sur le compte. */
  twoFactorEnabled?: boolean;
}

/**
 * - `second-factor` : mot de passe accepté, le compte demande le code de
 *   l'application d'authentification ;
 * - `enrollment` : la double authentification est obligatoire pour ce compte
 *   et pas encore activée (cahier des charges §9).
 * Dans ces deux états, l'API refuse les routes admin : seul l'écran de
 * connexion s'affiche.
 */
export type SessionStatus = 'booting' | 'anonymous' | 'second-factor' | 'enrollment' | 'authenticated';

interface EtatDoubleAuth {
  enabled: boolean;
  required: boolean;
  verified: boolean;
}

export interface SessionState {
  status: SessionStatus;
  user: AdminUser | null;
  /** Message à afficher sur l'écran de connexion (session expirée, inactivité…). */
  notice: string | null;
}

const CLE_REFRESH = 'mbg-admin.refresh';
const CLE_ACTIVITE = 'mbg-admin.activity';

export const INACTIVITE_MAX_MS = 8 * 60 * 60 * 1000;
/** Marge avant expiration du jeton d'accès pour le renouveler. */
const MARGE_RENOUVELLEMENT_MS = 30_000;

let jetonAcces: string | null = null;
let renouvellementEnCours: Promise<string | null> | null = null;
let etat: SessionState = { status: 'booting', user: null, notice: null };
const abonnes = new Set<() => void>();

function publier(suivant: SessionState): void {
  etat = suivant;
  abonnes.forEach((f) => f());
}

export function getSessionState(): SessionState {
  return etat;
}

export function subscribeSession(f: () => void): () => void {
  abonnes.add(f);
  return () => abonnes.delete(f);
}

/** Date d'expiration (ms) d'un JWT, ou null s'il est illisible. Aucune vérification de signature : l'API s'en charge. */
export function expirationJwt(jeton: string): number | null {
  const morceau = jeton.split('.')[1];
  if (!morceau) return null;
  try {
    const base64 = morceau.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(morceau.length / 4) * 4, '=');
    const charge = JSON.parse(atob(base64)) as { exp?: unknown };
    return typeof charge.exp === 'number' ? charge.exp * 1000 : null;
  } catch {
    return null;
  }
}

function lireStockage(cle: string): string | null {
  try {
    return sessionStorage.getItem(cle);
  } catch {
    return null;
  }
}

function ecrireStockage(cle: string, valeur: string | null): void {
  try {
    if (valeur === null) sessionStorage.removeItem(cle);
    else sessionStorage.setItem(cle, valeur);
  } catch {
    // Stockage bloqué (navigation privée stricte) : la session tient jusqu'au rechargement.
  }
}

/** Note une activité de l'admin (clic, frappe). Écrit au plus une fois par minute. */
export function noterActivite(maintenant = Date.now(), forcer = false): void {
  const derniere = Number(lireStockage(CLE_ACTIVITE) ?? 0);
  if (forcer || maintenant - derniere > 60_000) ecrireStockage(CLE_ACTIVITE, String(maintenant));
}

export function estInactif(maintenant = Date.now()): boolean {
  const derniere = Number(lireStockage(CLE_ACTIVITE) ?? 0);
  return derniere > 0 && maintenant - derniere > INACTIVITE_MAX_MS;
}

/** Ferme la session localement ; `notice` s'affiche sur l'écran de connexion. */
export function logout(notice: string | null = null): void {
  jetonAcces = null;
  renouvellementEnCours = null;
  ecrireStockage(CLE_REFRESH, null);
  ecrireStockage(CLE_ACTIVITE, null);
  publier({ status: 'anonymous', user: null, notice });
}

/** Appelé par le client API sur un 401 : le jeton n'est plus accepté. */
export function expireSession(): void {
  if (etat.status === 'authenticated') logout('Session expirée. Reconnectez-vous.');
}

async function renouveler(): Promise<string | null> {
  const refresh = lireStockage(CLE_REFRESH);
  if (!refresh) return null;
  const { data } = await publicApi.POST('/api/auth/refresh', { body: { refreshToken: refresh } });
  if (!data) return null;
  jetonAcces = data.token;
  ecrireStockage(CLE_REFRESH, data.refreshToken);
  return jetonAcces;
}

/** Jeton d'accès valable au moins 30 s, renouvelé si besoin (un seul renouvellement à la fois). */
export async function getAccessToken(maintenant = Date.now()): Promise<string | null> {
  const expiration = jetonAcces ? expirationJwt(jetonAcces) : null;
  if (jetonAcces && expiration && expiration - maintenant > MARGE_RENOUVELLEMENT_MS) return jetonAcces;
  renouvellementEnCours ??= renouveler().finally(() => {
    renouvellementEnCours = null;
  });
  return renouvellementEnCours;
}

export class ErreurConnexion extends Error {}

/** Étape après le mot de passe : code demandé, enrôlement obligatoire, ou session ouverte. */
export function etapeDoubleAuth(etat: EtatDoubleAuth | undefined): SessionStatus {
  if (etat?.enabled && !etat.verified) return 'second-factor';
  if (etat?.required && !etat.enabled) return 'enrollment';
  return 'authenticated';
}

function publierCompte(user: AdminUser, etape: SessionStatus): void {
  publier({ status: etape, user: { ...user, twoFactorEnabled: etape === 'second-factor' ? true : user.twoFactorEnabled }, notice: null });
}

function enregistrerJetons(jetons: { token: string; refreshToken: string }): void {
  jetonAcces = jetons.token;
  ecrireStockage(CLE_REFRESH, jetons.refreshToken);
}

/** En-tête d'autorisation pour les routes de double authentification (hors client admin). */
async function autorisation(): Promise<{ Authorization: string }> {
  const jeton = await getAccessToken();
  if (!jeton) {
    logout('Session expirée. Reconnectez-vous.');
    throw new ErreurConnexion('Session expirée. Reconnectez-vous.');
  }
  return { Authorization: `Bearer ${jeton}` };
}

/** Connexion : refusée si le compte n'est pas administrateur, sans rien stocker. */
export async function login(email: string, motDePasse: string): Promise<AdminUser> {
  const { data, error, response } = await publicApi.POST('/api/auth/login', {
    body: { email: email.trim(), password: motDePasse },
  });
  if (!data) {
    throw new ErreurConnexion(
      response?.status === 401 ? 'E-mail ou mot de passe incorrect.' : messageErreur(error, response)
    );
  }
  if (!data.user.isAdmin) {
    throw new ErreurConnexion('Ce compte n’a pas accès au back-office.');
  }
  enregistrerJetons(data);
  noterActivite(Date.now(), true);
  const user: AdminUser = {
    id: data.user.id,
    email: data.user.email,
    username: data.user.username ?? null,
    isAdmin: data.user.isAdmin,
    isSuperAdmin: data.user.isSuperAdmin,
    twoFactorEnabled: data.twoFactor?.enabled ?? false,
  };
  publierCompte(user, etapeDoubleAuth(data.twoFactor));
  return user;
}

/** Code de l'application (ou code de secours) : la session s'ouvre. */
export async function verifierDoubleAuth(code: string): Promise<void> {
  const { data, error, response } = await publicApi.POST('/api/auth/2fa/verify', {
    headers: await autorisation(),
    body: { code: code.replace(/\s+/g, '') },
  });
  if (!data) throw new ErreurConnexion(messageErreur(error, response));
  enregistrerJetons(data);
  if (etat.user) publierCompte({ ...etat.user, twoFactorEnabled: true }, 'authenticated');
}

/** Nouveau secret à scanner (enrôlement ou changement de téléphone). */
export async function preparerDoubleAuth(): Promise<{ secret: string; otpauthUrl: string }> {
  const { data, error, response } = await publicApi.POST('/api/auth/2fa/setup', { headers: await autorisation() });
  if (!data) throw new ErreurConnexion(messageErreur(error, response));
  return { secret: data.secret, otpauthUrl: data.otpauthUrl };
}

/**
 * Premier code de l'application : la double authentification est activée,
 * la session passe « avec code ». Renvoie les codes de secours, montrés une
 * seule fois ; pendant l'enrôlement obligatoire, la session s'ouvre quand
 * l'admin confirme les avoir notés (terminerEnrolement).
 */
export async function activerDoubleAuth(code: string): Promise<string[]> {
  const { data, error, response } = await publicApi.POST('/api/auth/2fa/enable', {
    headers: await autorisation(),
    body: { code: code.replace(/\s+/g, '') },
  });
  if (!data) throw new ErreurConnexion(messageErreur(error, response));
  enregistrerJetons(data);
  if (etat.user) publier({ ...etat, user: { ...etat.user, twoFactorEnabled: true } });
  return data.recoveryCodes;
}

export function terminerEnrolement(): void {
  if (etat.status === 'enrollment' && etat.user) publierCompte(etat.user, 'authenticated');
}

/** Retire la double authentification du compte (code demandé) ; la session continue sans code. */
export async function desactiverDoubleAuth(code: string): Promise<void> {
  const { data, error, response } = await publicApi.POST('/api/auth/2fa/disable', {
    headers: await autorisation(),
    body: { code: code.replace(/\s+/g, '') },
  });
  if (!data) throw new ErreurConnexion(messageErreur(error, response));
  enregistrerJetons(data);
  if (etat.user) publier({ ...etat, user: { ...etat.user, twoFactorEnabled: false } });
}

/**
 * Appelé par le client API sur un 403 MFA_REQUIRED / MFA_SETUP_REQUIRED : la
 * double authentification a été activée ailleurs (autre onglet, réglage du
 * super admin). La session repasse par l'écran de connexion, sans la fermer.
 */
export function exigerDoubleAuth(code: 'MFA_REQUIRED' | 'MFA_SETUP_REQUIRED'): void {
  if (etat.status !== 'authenticated' || !etat.user) return;
  publier({
    status: code === 'MFA_REQUIRED' ? 'second-factor' : 'enrollment',
    user: code === 'MFA_REQUIRED' ? { ...etat.user, twoFactorEnabled: true } : etat.user,
    notice: code === 'MFA_REQUIRED' ? 'Saisissez votre code pour continuer.' : null,
  });
}

/**
 * Au chargement : reprend la session de l'onglet si le jeton de renouvellement
 * est encore valable, que l'admin n'a pas dépassé 8 h d'inactivité et qu'il
 * est toujours administrateur.
 */
export async function boot(): Promise<void> {
  try {
    await reprendre();
  } catch {
    // API injoignable : on garde le jeton de l'onglet pour réessayer au rechargement.
    jetonAcces = null;
    publier({ status: 'anonymous', user: null, notice: 'Serveur injoignable. Réessayez dans un instant.' });
  }
}

async function reprendre(): Promise<void> {
  if (!lireStockage(CLE_REFRESH)) {
    publier({ status: 'anonymous', user: null, notice: null });
    return;
  }
  if (estInactif()) {
    logout('Session fermée après 8 h d’inactivité.');
    return;
  }
  const jeton = await getAccessToken();
  if (!jeton) {
    logout('Session expirée. Reconnectez-vous.');
    return;
  }
  const { data } = await publicApi.GET('/api/auth/me', { headers: { Authorization: `Bearer ${jeton}` } });
  if (!data || !data.user.isAdmin) {
    logout(data ? 'Ce compte n’a plus accès au back-office.' : 'Session expirée. Reconnectez-vous.');
    return;
  }
  noterActivite(Date.now(), true);
  publierCompte(
    {
      id: data.user.id,
      email: data.user.email,
      username: data.user.username,
      isAdmin: data.user.isAdmin,
      isSuperAdmin: data.user.isSuperAdmin,
      twoFactorEnabled: data.twoFactor?.enabled ?? data.user.twoFactorEnabled ?? false,
    },
    etapeDoubleAuth(data.twoFactor)
  );
}

/** Réservé aux tests : remet le module à zéro. */
export function __reinitialiserSession(): void {
  jetonAcces = null;
  renouvellementEnCours = null;
  etat = { status: 'booting', user: null, notice: null };
  abonnes.clear();
}
